import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Element } from '../../../../../common/html/element'
import { Panel } from '../../../../../common/panel'
import { Table } from '../../../../../common/table'
import { expiryDate } from '../../../../../dashboard/components/currentPlan/helper'
import { formatText } from '../../../../../helpers/value'
import { CriteriaInfo } from '../../../../../tool/screening/common/criteriaInfo'
import {
  selectIsLoadingSomeData,
  selectScreeningIndicatorById,
} from '../../../../../tool/screening/common/store/slice'
import {
  getCompanyStatus,
  getCompanyType,
  getICB,
  getLegalForm,
  getListingStatus,
  getLocation,
  getScreeningIndicators,
  getVSIC,
  getYearReports,
} from '../../../../../tool/screening/common/store/thunk'
import { EMPTY_VALUE } from '../../../../../utils/common'
import { COMPLETED_STATUS, PAYMENT_METHOD_PACKAGE } from '../../constants'
import { DateOrderedCell } from '../../table/DateOrderedCell'
import { OrderStatusCell } from '../../table/OrderStatusCell'
import {
  selectIsLoading,
  selectScreeningCellData,
  selectScreeningData,
  selectScreeningIds,
} from '../store/slice'
import { DownloadCell } from './table/DownloadCell'
import { FileType } from './table/FileType'
import { PriceCell } from './table/PriceCell'

const schema = {
  NO: {
    colId: 'id',
    title: '#',
  },
  TYPE: {
    colId: 'fiinGateReportTypeName',
    title: 'TYPE',
  },
  SELECTED_CRITERIA: {
    colId: 'rawScreeningCriteria',
    title: 'SELECTED_CRITERIA',
  },
  TOTAL_INFORMATION: {
    colId: 'screeningCriteria',
    title: 'TOTAL_INFORMATION',
  },
  SORT_BY: {
    colId: 'screeningCriteriaSortBy',
    title: 'SORT_BY',
  },
  FILE_TYPE: {
    colId: 'fileType',
    title: 'FILE_TYPE',
  },
  DELIVERY_DATE: {
    colId: 'expectedDeliveryDate',
    title: 'DELIVERY_DATE',
  },
  STATUS: {
    colId: 'status',
    title: 'ORDER_STATUS',
  },
  PRICE: {
    colId: 'priceVND',
    title: 'PRICE',
  },
  FILE: {
    colId: 'orderDetailId',
    title: 'FILE',
  },
}

export const Screening = ({ isPdf = false }) => {
  const dispatch = useDispatch()

  const ids = useSelector(selectScreeningIds)
  const isLoading = useSelector(selectIsLoading)
  const locale = useSelector((state) => state.i18n.locale)
  const isLoadingScreeningData = useSelector(selectIsLoadingSomeData)
  const screeningDataTable = useSelector(selectScreeningData)
  const screeningIndicatorById = useSelector(selectScreeningIndicatorById())

  const convertedData = (data) => {
    if (!data?.length) {
      return []
    }

    const dataInfo = data.map((item) => ({
      ...item,

      ...screeningIndicatorById[item.screeningIndicatorId],
    }))

    return dataInfo.filter(
      (item, index) =>
        dataInfo.findIndex(
          (view) =>
            view.screeningIndicatorName === item.screeningIndicatorName &&
            view.year === item.year,
        ) === index,
    )
  }

  const renderContent = () => {
    return (
      !!ids.length && (
        <div className="mt2">
          <Table
            ids={ids}
            isNoDataWithHead
            isLoading={isLoading || isLoadingScreeningData}
            getDataFromRedux={selectScreeningCellData}
            isDisplayScroll={false}
            isNoStickyHead={isPdf}
            schema={Object.keys(schema)
              .filter((key) => !isPdf || key !== 'FILE')
              .map((key) => {
                const result = {
                  colId: schema[key].colId,
                  title:
                    'userManagement.orderManagement.orderDetail.' +
                    schema[key].title,
                  isI18n: true,
                  isHtml: true,
                  thStyle: {
                    textAlign: 'left',
                  },
                  render: (val, { locale, rowId }) => {
                    switch (schema[key].colId) {
                      case schema.DELIVERY_DATE.colId:
                        return <DateOrderedCell val={val} locale={locale} />
                      case schema.FILE.colId:
                        const isNotCompletedStatus =
                          screeningDataTable[rowId - 1]?.status !==
                          COMPLETED_STATUS
                        const isExpired =
                          expiryDate(
                            screeningDataTable[rowId - 1]?.expiryDate,
                          ) <= 0
                        return (
                          <DownloadCell
                            val={val}
                            isDisabled={isNotCompletedStatus}
                            isExpired={isExpired}
                          />
                        )
                      case schema.STATUS.colId:
                        return <OrderStatusCell val={val} />
                      case schema.PRICE.colId:
                        const companiesNo =
                          screeningDataTable[rowId - 1]?.numOfCompanies
                        const price = companiesNo ? companiesNo * val : 0
                        return (
                          <>
                            {screeningDataTable[rowId - 1]?.paymentMethod ===
                            PAYMENT_METHOD_PACKAGE ? (
                              <></>
                            ) : (
                              <PriceCell
                                val={price}
                                rowId={rowId}
                                getDataFromRedux={selectScreeningCellData}
                              />
                            )}
                          </>
                        )
                      case schema.FILE_TYPE.colId:
                        return <FileType fileType={val} />
                      case schema.SELECTED_CRITERIA.colId:
                        return (
                          val && (
                            <CriteriaInfo
                              criteria={val ? JSON.parse(val) : {}}
                              autoHeight
                              autoHeightMax={450}
                              appendStyle={{ backgroundColor: 'unset' }}
                            />
                          )
                        )
                      case schema.TOTAL_INFORMATION.colId:
                        const data = convertedData(JSON.parse(val)?.views)
                        return (
                          <>
                            <Element className="fw600">
                              <Translate value="screening.DEFAULT_INFO" />
                            </Element>

                            {!!data.length &&
                              data.map((item, index) => {
                                return (
                                  <>
                                    <Element
                                      key={index}
                                      className="fw600"
                                      elementName="p"
                                      style={{ padding: '5px 0' }}
                                    >
                                      {`${item?.screeningIndicatorName} ( ${item?.year} )`}
                                    </Element>
                                  </>
                                )
                              })}
                          </>
                        )
                      case schema.SORT_BY.colId:
                        const screeningData = screeningDataTable?.find(
                          (item) => item.id === rowId,
                        )?.screeningCriteria

                        const sortByData = screeningData
                          ? JSON.parse(screeningData)?.sortBy
                          : {}
                        const sortByName =
                          screeningIndicatorById[
                            sortByData?.screeningIndicatorId
                          ]?.screeningIndicatorName

                        return (
                          <>
                            {sortByName
                              ? `${formatText(sortByName)} (${
                                  sortByData?.sortDirection
                                })`
                              : EMPTY_VALUE}
                          </>
                        )
                      default:
                        return formatText(val)
                    }
                  },
                }

                return result
              })}
          />
        </div>
      )
    )
  }

  useEffect(() => {
    if (!isPdf) {
      dispatch(getScreeningIndicators())
      dispatch(getVSIC())
      dispatch(getICB())
      dispatch(getLocation())
      dispatch(getCompanyStatus())
      dispatch(getLegalForm())
      dispatch(getCompanyType())
      dispatch(getListingStatus())
    }
  }, [locale])

  useEffect(() => {
    if (!isPdf) {
      dispatch(getYearReports())
    }
  }, [])

  if (isPdf) {
    return (
      <Panel
        label="userManagement.orderManagement.orderDetail.ORDER_DETAILS"
        isI18n
      >
        {renderContent()}
      </Panel>
    )
  }

  return <div className="ádaskdghaskdhg">{renderContent()}</div>
}
